import React from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";
import getSelectedMetrics from "../../../utils/get-selected-metrics";

import { PERIOD_FORMATS } from "../../../constants";
import { useRillContext } from "../../../rill/RillReact/share/base";

const PERIOD_OPTIONS = (t) => ({
  day: [{ text: t("numberOfLifts.daily"), value: "day" }],
  week: [{ text: t("numberOfLifts.weekly"), value: "week" }],
  month: [{ text: t("numberOfLifts.monthly"), value: "month" }],
});

function View(props) {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();

  _.defaults(locals.query, {
    type: "total",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units = "metric" } = query;
  const user = _.get(snapshot, "user", {});
  const exercisesLifts = _.get(snapshot, "data.exercisesLifts", {});
  const period = (query.period = _.get(snapshot, "data.period", "day"));
  const period_short = PERIOD_FORMATS[period];
  const startDate = momentTZ(_.get(snapshot, "data.start", momentTZ()));
  const endDate = momentTZ(_.get(snapshot, "data.end", momentTZ()));
  const selectedMetrics = getSelectedMetrics(exercisesLifts, query.type);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);

  const liftSeries = _.map(selectedMetrics, (data, name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (key) => _.get(data, key)),
    };
  });
  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(liftSeries, `data.${i}`))
  );
  const maxVolume = _.max(totalVolumes);

  return (
    <div className="body metrics">
      <h1 className="alt">{t("numberOfLifts.title")}</h1>
      <p>
        {t("numberOfLifts.description")}
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={PERIOD_OPTIONS(t)[period]}
              />
            </div>

            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("numberOfLifts.kg"), value: "metric" },
                  { text: t("numberOfLifts.lbs"), value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              <ToggleLinksInput
                className="tabs"
                name="type"
                options={[
                  { text: t("numberOfLifts.total"), value: "total" },
                  { text: t("numberOfLifts.squat"), value: "squat" },
                  { text: t("numberOfLifts.bench"), value: "bench" },
                  { text: t("numberOfLifts.deadlift"), value: "deadlift" },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(selectedMetrics) && (
            <div>
              <div className="axis-labels">
                <div className="left">{t("numberOfLifts.lifts")}</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: liftSeries }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        referenceValue: 0,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: liftSeries,
                    }}
                    options={{
                      width: `${_.size(keys) * 40}px`,
                      height: "380px",
                      chartPadding: { left: 35 },
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        referenceValue: 0,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = startDate.clone().add(key, period);
                      const dayRange = getDayRangeString(
                        [date, date.clone().add(1, period).subtract(1, "day")],
                        query
                      );
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            {_.reverse(
                              _.compact(
                                _.map(liftSeries, ({ data, name }) => {
                                  const totalLifts = data[i];
                                  if (!totalLifts) return;
                                  return (
                                    <div key={name}>
                                      {_.capitalize(name)} <b>{totalLifts}</b>
                                    </div>
                                  );
                                })
                              )
                            )}
                            <div>
                              {t("numberOfLifts.total")}{" "}
                              <b>
                                {locals.withWeight(
                                  locals.fromMetric(totalVolumes[i], units),
                                  units
                                )}
                              </b>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default View;
